import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { Observable } from 'rxjs';
import html2canvas from 'html2canvas';
import { DataExportType, ExcelExportService } from 'app/shared/services/export/excel/excel-export.service';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-export-chart',
    templateUrl: './export-chart.component.html',
    styleUrls: ['./export-chart.component.scss'],
    standalone: false
})
export class ExportChartComponent implements OnChanges {
  @Input() fileName: string;
  @Input() chartId: string;
  @Input() orientation: 'p' | 'l' = 'l';
  @Input() data?: boolean = false;
  @Input() dataToExport?: any;
  @Input() useTable = true;
  @Input() multipleSheets = false;
  @Input() splitKey?: string;
  @Input() disabled = false;
  @Input() marginInline = 0;
  @Input() media = true;
  @Input() chartInLibCard = true; // if the chart is in a lib card select inner div this avoid fullscreen errors
  @Input() columnWidth: {wpx: number}[] = [];
  @Input() size = 'small';
  @Input() includeMarkers = false;

  private theme: string;
  public theme$: Observable<EThemeName>;
  public exporting = false;

  public options = [ 'image', 'pdf' ];


  constructor(private themeService: ThemeService, private excelService: ExcelExportService) {
    this.theme$ = this.themeService.getTheme();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if(this.data && this.media){this.options = ['.xlsx', '.csv', 'image', 'pdf']}
    else if(this.data && !this.media){this.options = ['.xlsx', '.csv']}
    else this.options = ['image', 'pdf'];
  }

  public onDropdownSelected({option}) {
    this.exporting = true;

    switch(option){
      case 'image':
        setTimeout(() => this.generatePng(), 50)
        break;
      case 'pdf':
        setTimeout(() => this.generatePdf(), 50) 
        break;
      case '.xlsx':
        setTimeout(() => this.generateDataExport(DataExportType.xlsx), 50);
        break;
      case '.csv':
        setTimeout(() => this.generateDataExport(DataExportType.csv), 50);
        break;
    }
  }

  private async generateDataExport(type: DataExportType) {
    try {
      const dataToExport = typeof this.dataToExport === 'function' ? await this.dataToExport() : this.dataToExport;
      if (this.useTable) this.excelService.exportTableAsFile(this.dataToExport, this.fileName, type);
      else if (!this.multipleSheets) this.excelService.exportAsExcelFile(dataToExport, this.fileName, type -1, this.columnWidth);
      else this.excelService.exportAsExcelFileWithMultipleSheets(dataToExport,this.fileName,type,this.splitKey)
    } finally {
      this.exporting = false;
    }
  
  }

  public generatePng() {
    const element = this.getChartElement()
    const { height, width } = element.getBoundingClientRect()
    const scale = window.devicePixelRatio 
    const offscreencanvas = new OffscreenCanvas(width*scale, height*scale);

    html2canvas(element,{
      // @ts-ignore
      canvas: offscreencanvas,
      scale: scale,
      ignoreElements: (element) => {
        return element.classList.contains('highcharts-crosshair') ||
          (!this.includeMarkers && element.classList.contains('highcharts-markers'));
      },
      backgroundColor: this.themeService.getColor('--card')

    }).finally(async () => {  
      const blob = await offscreencanvas.convertToBlob({ type: 'image/png' }).then(b =>b.arrayBuffer())
      const arrbuff = new Uint8Array(blob);
      saveAs(new Blob([arrbuff]), `${this.fileName}.png`);

      element.classList.remove(this.theme);
      this.exporting = false;
      this.toggleHiddenBlock(element, '.no-print', 'block');
    })

  }

  private getChartElement() : HTMLElement {
    const element = this.chartInLibCard
      ? document.querySelector<HTMLElement>(`#${this.chartId} > div`)
      : document.getElementById(this.chartId);

    if (element) {
      this.toggleHiddenBlock(element, '.no-print', 'none');
    }

    this.getCurrentTheme();
    element.classList.add(this.theme);
    return element;
  }

  public generatePdf() {
    const element = this.getChartElement();
    const { height, width } = element.getBoundingClientRect();
    const scale = window.devicePixelRatio;
    const offscreencanvas = new OffscreenCanvas(width*scale, height*scale);
    const [imgWidth,pageHeight] = this.orientation === "l" ? [290-this.marginInline*2,210] : [210-this.marginInline*2,290];
    const pixel2mm = imgWidth / offscreencanvas.width;
    const imgHeight = offscreencanvas.height * pixel2mm;


    html2canvas(element,{
      // @ts-ignore
      canvas: offscreencanvas,
      scale:scale,
      backgroundColor:this.themeService.getColor('--card')
      

    }).finally(async () => {  
      
      const blob = await offscreencanvas.convertToBlob({ type: 'image/jpeg' }).then(b =>b.arrayBuffer())
      const arrbuff = new Uint8Array(blob);
      const pdf = new jsPDF(this.orientation, 'mm', 'a4', true); 
      let remainingHeight = imgHeight ;
      let position = 0;
      let i = 0;
      while(remainingHeight > 0){
        if (i) pdf.addPage();
        pdf.addImage(arrbuff, 'JPEG', this.marginInline, -position , imgWidth, imgHeight,null ,'FAST')
        remainingHeight -= pageHeight;
        position += pageHeight;
        i++;
      }
      pdf.save(`${this.fileName}.pdf`);

      element.classList.remove(this.theme);
      this.exporting = false;
      this.toggleHiddenBlock(element, '.no-print', 'block');
    });
  }
  
  private getCurrentTheme() {
    this.theme$.pipe(take(1)).subscribe(theme => this.theme = theme);
  }

  public toggleHiddenBlock(element: HTMLElement, cssClass: string, display: 'none' | 'block' = 'none') {
    const noPrintEl = element.querySelector<HTMLElement>(cssClass);
    if (noPrintEl) {
      noPrintEl.style.display = display;
    }
  }
}
